/**
 * How a tower's weapon turns (owner report 2026-10-06: "the cannons snap"). Pure maths, no three.js, so it is tested
 * without WebGL; views/towers.ts drives it per frame and writes the result on the `weapon` node (aimWeapon).
 *
 * The weapon no longer jumps to each shot's bearing. Every frame it slews toward its current target's (led) position
 * along the shortest arc with a critically damped spring (`smoothDampAngle`: eases in and out, never overshoots a
 * still target) capped at a turn rate per tower — an archer's bow swings fast, a bronze bombard grinds round. It keeps
 * tracking between shots, and with no target in reach it relaxes into a sentry's slow look-around about the last
 * bearing it fought on (`idleLook`). A shot that comes while the weapon is still off target turns it urgently, and the
 * shot's visuals wait up to `MaxReleaseDelay` for it (`releaseDelay`); whatever is still left is bent out of the
 * barrel by the round's flight (projectiles.ts `bend`). The sim never reads any of this: it has already decided the hit.
 */

const TAU = Math.PI * 2;

/** An angle wrapped into (−π, π]. */
export function wrapAngle(a: number): number {
  const w = a - TAU * Math.floor((a + Math.PI) / TAU);
  return w <= -Math.PI ? w + TAU : w;
}

/** The signed shortest turn from `from` to `to` (radians, in (−π, π]). */
export function shortestArc(from: number, to: number): number { return wrapAngle(to - from); }

/**
 * A spring carried between frames: its value and velocity, the rate-limited goal it chases (unset: its value, at rest
 * — the goal walks from there) and the stiffness it last chased with.
 */
export interface Spring { value: number; vel: number; goal?: number; omega?: number }

/**
 * The chasing spring's ω × `smooth`. The specs were tuned on a spring that went for the target itself (ω = 2/smooth);
 * chasing the walking goal, 3 keeps their turn times (a cannon's quarter turn 0.67 s, half turn 1.1 s).
 */
const STIFFNESS = 3;

/**
 * One step of a rate-limited critically damped spring toward `target`: a goal walks to the target at `maxRate` and a
 * critically damped spring (settling in about `smooth` seconds; STIFFNESS) chases the goal. Both are integrated
 * exactly over the frame — the goal may arrive part-way through it — so the turn is the same at 24 fps as at 240 (the
 * SmoothDamp approximation it replaced ran up to 9° behind at the capture's 24 fps, owner report 2026-10-13). From
 * rest it eases in and out, never passes the target and never turns faster than `maxRate`: its speed is the goal's
 * smoothed by the spring's impulse response, which is positive and sums to one. Stable at any `dt`. Writes `s` and
 * returns its new value.
 */
export function smoothDamp(s: Spring, target: number, smooth: number, maxRate: number, dt: number): number {
  if (!(dt > 0)) return s.value;
  const omega = STIFFNESS / Math.max(1e-4, smooth);
  let g = s.goal ?? s.value;
  // a new stiffness (a shot hurrying the weapon) keeps the spring's lag behind its goal in proportion, so it doesn't
  // lunge at a goal it had been trailing on the softer spring (owner report 2026-10-13: a 27° jump on the poison tower)
  if (s.omega !== undefined && s.omega !== omega) {
    const k = s.value + ((g - s.value) * s.omega) / omega;
    g = (target - g) * (target - k) < 0 ? target : k;
  }
  s.omega = omega;
  const gap = target - g;
  const rate = maxRate > 0 && gap !== 0 ? maxRate : 0;
  const walk = rate > 0 ? Math.min(dt, Math.abs(gap) / rate) : dt;
  const u = Math.sign(gap) * rate;
  chase(s, g, u, omega, walk);
  if (walk < dt) { chase(s, target, 0, omega, dt - walk); s.goal = target; }
  else s.goal = g + u * walk;
  return s.value;
}

/**
 * The exact critically damped chase of a goal moving at `u` from `g0` over `t` seconds. Measured from the goal less
 * its steady lag 2u/ω, the spring's offset y obeys y'' = −ω²y − 2ωy': y(t) = (y0 + (y0' + ωy0) t) e^(−ωt).
 */
function chase(s: Spring, g0: number, u: number, omega: number, t: number): void {
  const lag = (2 * u) / omega;
  const y0 = s.value - g0 + lag, dy0 = s.vel - u;
  const e = Math.exp(-omega * t), k = dy0 + omega * y0;
  s.value = g0 + u * t - lag + (y0 + k * t) * e;
  s.vel = u + (dy0 - omega * k * t) * e;
}

/** smoothDamp on an angle: the goal heads for the target along the shortest arc; the result wrapped into (−π, π]. */
export function smoothDampAngle(s: Spring, target: number, smooth: number, maxRate: number, dt: number): number {
  const goal = s.value + wrapAngle((s.goal ?? s.value) - s.value);
  s.goal = goal;
  smoothDamp(s, goal + shortestArc(goal, target), smooth, maxRate, dt);
  const w = wrapAngle(s.value);
  s.goal += w - s.value;
  s.value = w;
  return w;
}

/** A tower's weapon handling. */
export interface AimSpec {
  /** Seconds the spring takes to settle on a still target while tracking. */
  readonly smooth: number;
  /** The fastest the weapon turns (rad/s). */
  readonly maxRate: number;
  /** The spring's time when a shot comes while it is off target (and the cap times `urgentRate`, at most URGENT_RATE_CAP). */
  readonly urgent: number;
  readonly urgentRate: number;
  /** Elevation with range, for guns that may pitch: radians per cell past `pitchFrom` cells, at most `pitchMax`. */
  readonly pitchPerCell: number; readonly pitchFrom: number; readonly pitchMax: number;
  /** Half the arc of the idle look-around (radians), and the spring's time while it looks. */
  readonly sweep: number; readonly sweepSmooth: number;
}

const base: AimSpec = { smooth: 0.16, maxRate: 5, urgent: 0.07, urgentRate: 2.2, pitchPerCell: 0, pitchFrom: 2, pitchMax: 0, sweep: 0.55, sweepSmooth: 0.7 };
/**
 * Per tower (and tier-IV branch model, by its `_t4_<branch>` suffix): an archer swings his bow round quickly; the
 * cannon's iron gun on its carriage turns heavily and lifts its muzzle a little for a far target; the mortar's
 * carriage grinds round (its barrel's pitch is the clip's and stays fixed); the bombard is the slowest of all, the
 * gatling the most nervous.
 */
export const AIM_SPECS: Readonly<Record<string, AimSpec>> = {
  archer: { ...base, smooth: 0.12, maxRate: 7, sweep: 0.7 },
  ballista: { ...base, smooth: 0.17, maxRate: 4.5, pitchPerCell: 0.012, pitchMax: 0.05, sweep: 0.5 },
  cannon: { ...base, smooth: 0.22, maxRate: 3.2, urgent: 0.09, pitchPerCell: 0.022, pitchMax: 0.11, sweep: 0.45, sweepSmooth: 0.9 },
  mortar: { ...base, smooth: 0.3, maxRate: 2.4, urgent: 0.12, sweep: 0.35, sweepSmooth: 1.1 },
  fire: { ...base, smooth: 0.14, maxRate: 6, sweep: 0.5 },
  poison: { ...base, smooth: 0.15, maxRate: 5.5, sweep: 0.5 },
  t4_marksman: { ...base, smooth: 0.15, maxRate: 5, pitchPerCell: 0.01, pitchMax: 0.04, sweep: 0.6 },
  t4_ranger: { ...base, smooth: 0.12, maxRate: 7, sweep: 0.6 },
  t4_bombard: { ...base, smooth: 0.32, maxRate: 2, urgent: 0.12, pitchPerCell: 0.02, pitchMax: 0.08, sweep: 0.35, sweepSmooth: 1.2 },
  t4_gatling: { ...base, smooth: 0.11, maxRate: 6, urgent: 0.06, sweep: 0.6, sweepSmooth: 0.5 },
  t4_lance: { ...base, smooth: 0.18, maxRate: 4.2, pitchPerCell: 0.012, pitchMax: 0.05, sweep: 0.45 },
  t4_repeater: { ...base, smooth: 0.13, maxRate: 5.5, sweep: 0.5 },
  t4_battery: { ...base, smooth: 0.32, maxRate: 2.2, urgent: 0.12, sweep: 0.35, sweepSmooth: 1.1 },
  t4_rocket: { ...base, smooth: 0.26, maxRate: 2.8, urgent: 0.11, sweep: 0.4, sweepSmooth: 1 },
  t4_phoenix: { ...base, smooth: 0.18, maxRate: 4.5, sweep: 0.8, sweepSmooth: 0.9 },
};
/**
 * The fastest any weapon turns, urgent or not (rad/s): the cannon's urgent swing. The light weapons' urgent rates
 * (an archer's 2.2 × 7 = 15 rad/s) whipped a bow 37° a frame at the capture's 24 fps — a snap to the eye (owner
 * report 2026-10-13); a weapon that tracks faster than this keeps its own rate.
 */
export const URGENT_RATE_CAP = 7;
/** The turn-rate cap while a shot hurries the weapon round. */
export function urgentRateOf(spec: AimSpec): number { return Math.max(spec.maxRate, Math.min(spec.maxRate * spec.urgentRate, URGENT_RATE_CAP)); }

/** The handling of a tower (`arch`) wearing model `model` (a tier-IV branch has its own when listed). */
export function aimSpecOf(arch: string, model = ""): AimSpec {
  const b = /_(t4_[a-z]+)(?:__[a-z0-9_]+)?$/.exec(model)?.[1];       // a skin's own branch model aims like the branch
  return (b ? AIM_SPECS[b] : undefined) ?? AIM_SPECS[arch] ?? base;
}

/** The pitch (radians, up) for a target `cells` away. */
export function pitchFor(spec: AimSpec, cells: number): number {
  return Math.max(0, Math.min(spec.pitchMax, (cells - spec.pitchFrom) * spec.pitchPerCell));
}

/** A small deterministic hash in [0, 1) of a seed and an index (the look-around's glances). */
export function hash01(seed: number, i: number): number {
  let h = (Math.imul(seed | 0, 0x9e3779b1) ^ Math.imul(i | 0, 0x85ebca77)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

/** Seconds a glance of the idle look-around holds before the next. */
export const GLANCE_SECONDS = 2.6;
/**
 * The bearing an idle weapon looks toward `t` seconds into its idle: glances about `home` (held `GLANCE_SECONDS`,
 * staggered per seed so neighbouring towers don't move together), within `spec.sweep` either side. The spring
 * (spec.sweepSmooth) eases between them, so the weapon pans, pauses, and pans on — a sentry, not a metronome.
 */
export function idleLook(spec: AimSpec, home: number, t: number, seed: number): number {
  const phase = hash01(seed, 0) * GLANCE_SECONDS;
  const k = Math.floor((t + phase) / GLANCE_SECONDS);
  const glance = (hash01(seed, k + 1) * 2 - 1) * spec.sweep;
  return wrapAngle(home + glance);
}

/** On target within this (radians, ~5°): the shot leaves as it is. */
export const ON_TARGET = 0.09;
/** The most a shot's visuals wait for the weapon to come round (s). Beyond it the round is bent out of the barrel. */
export const MAX_RELEASE_DELAY = 0.14;

/**
 * The seconds a shot's visuals should wait so that the weapon, turning urgently from `yaw` (moving at `vel`) toward
 * `target`, is on target by the round's release `release` seconds after the shot: 0 when it will be in time, at most
 * MAX_RELEASE_DELAY. Simulated at 60 Hz on the same spring the view runs (`from`: the rest of its Spring).
 */
export function releaseDelay(spec: AimSpec, yaw: number, vel: number, target: number, release: number, from: Readonly<Partial<Spring>> = {}): number {
  const s: Spring = { value: yaw, vel, goal: from.goal, omega: from.omega };
  const dt = 1 / 60, limit = release + MAX_RELEASE_DELAY;
  let t = 0;
  while (Math.abs(shortestArc(s.value, target)) > ON_TARGET && t < limit) { smoothDampAngle(s, target, spec.urgent, urgentRateOf(spec), dt); t += dt; }
  return Math.max(0, Math.min(MAX_RELEASE_DELAY, t - release));
}

/** Seconds of no target before the weapon gives up its last bearing and starts the look-around. */
export const IDLE_AFTER = 1.2;

/** One tower's aim, carried between frames by its view. */
export interface AimState {
  yaw: Spring; pitch: Spring;
  /** The bearing it relaxes about when idle (the last it fought on, or toward the road). */
  home: number;
  /** Seconds since it last had a target (Infinity: never), and its idle clock. */
  lost: number; idleT: number;
  /** Turning urgently for a shot until on target. */
  urgent: boolean;
  seed: number;
}
export function aimState(yaw: number, seed: number): AimState {
  return { yaw: { value: wrapAngle(yaw), vel: 0 }, pitch: { value: 0, vel: 0 }, home: wrapAngle(yaw), lost: Infinity, idleT: 0, urgent: false, seed };
}

/**
 * One frame of aiming. `bearing` is the yaw to the (led) target and `cells` its distance, or null with no target.
 * Returns the yaw and pitch the weapon wears this frame.
 */
export function stepAim(a: AimState, spec: AimSpec, bearing: number | null, cells: number, dt: number): { yaw: number; pitch: number } {
  if (bearing !== null) {
    a.lost = 0; a.idleT = 0; a.home = bearing;
    const urgent = a.urgent && Math.abs(shortestArc(a.yaw.value, bearing)) > ON_TARGET * 0.5;
    a.urgent = urgent;
    smoothDampAngle(a.yaw, bearing, urgent ? spec.urgent : spec.smooth, urgent ? urgentRateOf(spec) : spec.maxRate, dt);
    smoothDamp(a.pitch, pitchFor(spec, cells), spec.smooth * 1.5, 1, dt);
  } else {
    a.lost += dt; a.urgent = false;
    if (a.lost >= IDLE_AFTER) {
      a.idleT += dt;
      smoothDampAngle(a.yaw, idleLook(spec, a.home, a.idleT, a.seed), spec.sweepSmooth, spec.maxRate * 0.35, dt);
    } else smoothDampAngle(a.yaw, a.home, spec.smooth, spec.maxRate, dt);
    smoothDamp(a.pitch, 0, spec.sweepSmooth, 0.5, dt);
  }
  return { yaw: a.yaw.value, pitch: a.pitch.value };
}
